import { Link } from "@tanstack/react-router";
import {
  Check, X, ArrowRight, Phone, ShieldCheck, Clock, Hammer,
  Sparkles, Zap, Sun, Droplets, Palette, Star, type LucideIcon,
} from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { MultiStepQuoteForm } from "@/components/MultiStepQuoteForm";
import { FAQAccordion } from "@/components/FAQAccordion";
import { type Faq, withObjectionFaqs } from "@/lib/faqs";
import { SERVICES, SITE } from "@/lib/site";
import { SERVICE_TESTIMONIALS } from "@/lib/service-testimonials";
import { CityLinksStrip } from "@/components/CityLinksStrip";
import { scrollToBelowHeader } from "@/lib/utils";

export type ServicePageProps = {
  slug: string;
  name: string;
  hero: string;
  heroEyebrow: string;
  intro: string;
  sections: { title: string; body: string; image?: string; table?: { columns: string[]; rows: string[][] } }[];
  benefits: string[];
  bestFor: string[];
  faqs: Faq[];
  problems?: string[];
  results?: string[];
  whyCards?: { title: string; body: string }[];
  specs?: { label: string; value: string }[];
};

const DEFAULT_SPECS = [
  { label: "System", value: "Premium coating + polyaspartic topcoat" },
  { label: "Install time", value: "1 day" },
  { label: "Walk on", value: "4–6 hours" },
  { label: "Drive on", value: "18–24 hours" },
  { label: "Lifespan", value: "15–20+ years" },
  { label: "Warranty", value: "10-Year Warranty" },
  { label: "Pricing", value: "From $7–$12 / sq ft" },
];

const TRUST_CHIPS = [
  { icon: ShieldCheck, label: "10-Year Warranty" },
  { icon: Clock, label: "1-Day Install" },
  { icon: Hammer, label: "Diamond-Ground Prep" },
];

const WHY_ICONS: LucideIcon[] = [ShieldCheck, Sparkles, Zap, Sun, Droplets, Hammer, Clock, Palette];

// Section titles to drop (specs + color-options live in dedicated sections elsewhere)
const SKIP_SECTION = (t: string) =>
  /specifications?$/i.test(t) ||
  /^color options/i.test(t) ||
  /color palettes? & design/i.test(t);

const GOOGLE_COLORS = ["#4285f4", "#ea4335", "#fbbc04", "#34a853"];
const REVIEW_META = [
  { count: 12, when: "2 weeks ago" },
  { count: 47, when: "1 month ago" },
  { count: 8, when: "3 weeks ago" },
];

export function ServicePageLayout(p: ServicePageProps) {
  const related = SERVICES.filter((s) => s.slug !== p.slug).slice(0, 3);
  const problems = p.problems ?? [];
  const results = p.results ?? [];
  const whyCards = p.whyCards ?? [];
  const specs = p.specs ?? DEFAULT_SPECS;
  const visibleSections = p.sections.filter((s) => !SKIP_SECTION(s.title));

  return (
    <>
      {/* HERO — two-column with quote form */}
      <section className="relative isolate overflow-hidden border-b border-border bg-jet">
        <img src={p.hero} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-30 text-left" />
        <div className="absolute inset-0 bg-gradient-to-b from-jet/70 via-jet/85 to-jet" aria-hidden />
        <div className="absolute inset-0 grain opacity-40" aria-hidden />
        <div className="container-x relative py-20 md:py-24 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-copper">{p.heroEyebrow}</p>
              <span className="copper-rule mt-5" />
              <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl text-silver-light text-balance">{p.name}</h1>
              <p className="mt-5 text-lg text-silver max-w-2xl">{p.intro}</p>
              <div className="mt-8">
                <a
                  href={SITE.phoneHref}
                  className="hero-phone-cta inline-flex items-center justify-center gap-2 rounded-sm border px-7 py-4 text-sm font-bold uppercase tracking-wider transition-colors"
                >
                  <Phone className="h-4 w-4" /> {SITE.phone}
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                {TRUST_CHIPS.map((t) => (
                  <span key={t.label} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-silver">
                    <t.icon className="h-4 w-4 text-copper" /> {t.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:pl-4">
              <MultiStepQuoteForm
                heroMode
                onHeroSelect={() => {
                  const el = document.getElementById("bottom-quote-form");
                  if (el) scrollToBelowHeader(el);
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM / RESULT */}
      {(problems.length > 0 || results.length > 0) && (
        <section className="section-light section-ivory py-20 md:py-24">
          <div className="container-x grid gap-6 lg:grid-cols-2">
            <div className="rounded-sm border border-border bg-white p-8 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">The Problem</p>
              <h2 className="mt-4 text-2xl md:text-3xl text-foreground">Tired of dealing with your floor?</h2>
              <ul className="mt-6 space-y-3">
                {problems.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-foreground/80">
                    <X className="h-5 w-5 mt-0.5 text-copper shrink-0" /> <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-sm border border-white/10 bg-jet p-8 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">The MLC Epoxy Floors Result</p>
              <h2 className="mt-4 text-2xl md:text-3xl text-silver-light">A floor you'll love for 20+ years.</h2>
              <ul className="mt-6 space-y-3">
                {results.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-silver">
                    <Check className="h-5 w-5 mt-0.5 text-copper shrink-0" /> <span>{b}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#quote"
                className="mt-7 inline-flex items-center gap-2 rounded-sm bg-copper px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-copper-hover transition-colors"
              >
                Start My Project <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* WHY CARDS + BENEFITS/BEST FOR */}
      {whyCards.length > 0 && (
        <section className="border-y border-border bg-charcoal/60 py-20 md:py-24">
          <div className="container-x">
            <SectionHeading eyebrow={`Why ${p.name}`} title="Built specifically for performance." />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {whyCards.map((c, i) => {
                const Icon = WHY_ICONS[i % WHY_ICONS.length];
                return (
                  <div key={c.title} className="rounded-sm bg-ivory border border-border p-7 shadow-sm hover:shadow-md transition-shadow">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-copper/15 border border-copper/30 text-copper">
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="mt-5 text-sm font-bold uppercase tracking-widest text-charcoal">{c.title}</p>
                    <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">{c.body}</p>
                  </div>
                );
              })}
            </div>

            {(p.benefits.length > 0 || p.bestFor.length > 0) && (
              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {p.benefits.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-copper">Benefits</p>
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {p.benefits.map((b) => (
                        <li key={b} className="inline-flex items-center gap-1.5 rounded-sm border border-white/10 bg-jet px-3 py-1.5 text-xs text-silver">
                          <Check className="h-3.5 w-3.5 text-copper" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {p.bestFor.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-copper">Best For</p>
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {p.bestFor.map((b) => (
                        <li key={b} className="rounded-sm border border-white/10 bg-jet px-3 py-1.5 text-xs text-silver">
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* DEEP-DIVE SECTIONS */}
      <section className="section-light py-20 md:py-24">
        <div className="container-x max-w-5xl">
          <div className="space-y-16">
            {visibleSections.map((s, i) => {
              const reverse = i % 2 === 1;
              if (s.table) {
                return (
                  <article key={s.title} className="max-w-4xl">
                    <h2 className="text-2xl md:text-3xl text-foreground">{s.title}</h2>
                    <span className="copper-rule mt-3 mb-5" />
                    <div className="overflow-x-auto rounded-sm border border-border bg-card">
                      <table className="w-full text-left text-sm md:text-base text-foreground">
                        <thead className="bg-jet text-silver-light">
                          <tr>
                            {s.table.columns.map((c, ci) => (
                              <th
                                key={c}
                                className={`px-5 md:px-6 py-3 font-semibold uppercase tracking-wider text-xs ${ci === 0 ? "" : "border-l border-white/10"}`}
                              >
                                {c}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.table.rows.map((row, ri) => (
                            <tr key={ri} className={ri !== 0 ? "border-t border-border" : ""}>
                              {row.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className={`px-5 md:px-6 py-4 align-top ${ci === 0 ? "font-semibold text-foreground" : "text-foreground/80 border-l border-border"}`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {s.body && (
                      <p className="mt-5 text-foreground/75 leading-relaxed whitespace-pre-line">{s.body}</p>
                    )}
                  </article>
                );
              }
              if (!s.image) {
                return (
                  <article key={s.title} className="max-w-3xl">
                    <h2 className="text-2xl md:text-3xl text-foreground">{s.title}</h2>
                    <span className="copper-rule mt-3 mb-5" />
                    <p className="text-foreground/75 leading-relaxed whitespace-pre-line">{s.body}</p>
                  </article>
                );
              }
              return (
                <article key={s.title} className="grid gap-8 md:grid-cols-2 md:items-center">
                  <div className={reverse ? "md:order-2" : ""}>
                    <h2 className="text-2xl md:text-3xl text-foreground">{s.title}</h2>
                    <span className="copper-rule mt-3 mb-5" />
                    <p className="text-foreground/75 leading-relaxed whitespace-pre-line">{s.body}</p>
                  </div>
                  <div className={`overflow-hidden rounded-sm border border-border ${reverse ? "md:order-1" : ""}`}>
                    <img src={s.image} alt="" className="h-full w-full object-cover aspect-[4/3]" />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="section-light section-ivory py-20 md:py-24">
        <div className="container-x max-w-3xl">
          <SectionHeading eyebrow="Specs" title="The technical stuff." body="Every floor we install is engineered for Calgary Region conditions and built to outlast the slab beneath it." />
          <dl className="mt-10 overflow-hidden rounded-sm border border-border bg-card text-foreground">
            {specs.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-3 gap-4 px-5 md:px-7 py-4 ${i !== 0 ? "border-t border-border" : ""}`}
              >
                <dt className="text-xs font-semibold uppercase tracking-wider text-foreground/60">{row.label}</dt>
                <dd className="col-span-2 text-sm md:text-base font-medium text-foreground">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* REVIEWS — Google-style */}
      <section className="border-y border-border bg-charcoal/60 py-20 md:py-24">
        <div className="container-x">
          <SectionHeading eyebrow="Verified Customer Reviews" title="What Calgary Region customers say." />
          <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#dadce0] bg-white px-5 py-3">
            <span className="text-sm font-medium text-[#3c4043]">Customer Rating</span>
            <span className="text-base font-semibold text-[#202124]">5.0</span>
            <span className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4" fill="#fbbc04" color="#fbbc04" strokeWidth={0} />
              ))}
            </span>
            <span className="text-sm text-[#5f6368]">(127 reviews)</span>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {SERVICE_TESTIMONIALS.map((t, idx) => {
              const initial = t.name.trim().charAt(0).toUpperCase();
              const bg = GOOGLE_COLORS[idx % GOOGLE_COLORS.length];
              const meta = REVIEW_META[idx % REVIEW_META.length];
              const stars = t.stars ?? 5;
              return (
                <figure
                  key={t.name}
                  className="rounded-lg border border-[#dadce0] bg-white p-6 shadow-[0_1px_2px_rgba(60,64,67,0.08),0_1px_3px_rgba(60,64,67,0.12)]"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-base text-white"
                      style={{ backgroundColor: bg }}
                      aria-hidden
                    >
                      {initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#202124] leading-tight normal-case tracking-normal" style={{ fontFamily: "var(--font-sans)" }}>
                        {t.name}
                      </p>
                      <p className="text-xs text-[#5f6368] mt-0.5">Local Guide · {meta.count} reviews</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="flex gap-0.5">
                      {Array.from({ length: stars }).map((_, i) => (
                        <Star key={i} className="h-4 w-4" fill="#fbbc04" color="#fbbc04" strokeWidth={0} />
                      ))}
                    </span>
                    <span className="text-xs text-[#5f6368]">{meta.when}</span>
                  </div>
                  <blockquote className="mt-3 text-[#3c4043] text-sm leading-relaxed">{t.quote}</blockquote>
                  <figcaption className="mt-4 text-xs text-[#5f6368]">{t.city}</figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUOTE CTA */}
      <section id="quote" className="section-light section-ivory py-20 md:py-24 scroll-mt-24">
        <div className="container-x grid gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper">Get Your Free Quote</p>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl text-foreground text-balance">
              Now booking across the Calgary region.
            </h2>
            <p className="mt-5 text-foreground/75 text-lg">
              We're filling up fast. Lock in your spot today and start enjoying a cleaner, stronger, more beautiful floor that lasts a lifetime.
            </p>
            <ul className="mt-7 space-y-3 text-foreground/80">
              <li className="flex items-start gap-3"><Check className="h-5 w-5 mt-0.5 text-copper" /> No pressure, no obligation</li>
              <li className="flex items-start gap-3"><Check className="h-5 w-5 mt-0.5 text-copper" /> Free on-site measurement</li>
              <li className="flex items-start gap-3"><Check className="h-5 w-5 mt-0.5 text-copper" /> Detailed written quote in 24 hrs</li>
            </ul>
            <a
              href={SITE.phoneHref}
              className="mt-8 inline-flex items-center gap-3 rounded-sm border border-foreground/20 px-6 py-4 text-sm font-bold uppercase tracking-wider text-foreground hover:border-copper hover:text-copper transition-colors"
            >
              <Phone className="h-4 w-4 text-copper" /> Call for a free quote — {SITE.phone}
            </a>
          </div>
          <div id="bottom-quote-form" className="scroll-mt-6 rounded-md border border-border bg-ivory p-7 md:p-8 shadow-2xl">
            <MultiStepQuoteForm />
          </div>
        </div>
      </section>

      {/* FAQS */}
      <section className="section-light py-20 md:py-24">
        <div className="container-x">
          <SectionHeading eyebrow="Questions" title={`${p.name} FAQs`} />
          <div className="mt-10">
            <FAQAccordion items={withObjectionFaqs(p.faqs)} />
          </div>
        </div>
      </section>

      {/* CITY LINKS — local SEO internal linking */}
      <CityLinksStrip context="service" lead={`${p.name} installed across the Calgary Region —`} />

      {/* RELATED */}
      <section className="border-t border-border bg-charcoal/60 py-20">
        <div className="container-x">
          <SectionHeading eyebrow="Explore More" title="Other coating systems we install." />
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}` as string}
                className="group rounded-sm border border-border bg-jet p-6 hover:border-copper"
              >
                <p className="text-lg text-silver-light">{r.name}</p>
                <p className="mt-2 text-sm text-silver">{r.short}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-copper">
                  Learn More <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
